import { z } from 'zod';

export const diagnosisCodeSchema = z.object({
  code: z.string(),
  description: z.string().optional(),
});

export type DiagnosisCode = z.infer<typeof diagnosisCodeSchema>;

export const serviceCodeSchema = z.object({
  code: z.string(),
  description: z.string().optional(),
  units: z.number().optional(),
  placeOfService: z.string().optional(),
  effectiveFrom: z.string().optional(),
  effectiveTo: z.string().optional(),
});

export type ServiceCode = z.infer<typeof serviceCodeSchema>;

export const documentStatusSchema = z.enum([
  'approved',
  'denied',
  'pending',
  'cancelled',
  'modified',
]);

export type DocumentStatus = z.infer<typeof documentStatusSchema>;

export const documentReviewStatusSchema = z.enum(['draft', 'reviewed', 'approved']);

export type DocumentReviewStatus = z.infer<typeof documentReviewStatusSchema>;

/**
 * Timestamp fields cross the schema in two forms:
 *  - raw rows from Drizzle on the server (Date object)
 *  - already-serialized JSON on the client (ISO string)
 * Normalizing to ISO string lets the same schema validate in both places.
 */
const isoTimestamp = z.preprocess((v) => (v instanceof Date ? v.toISOString() : v), z.string());

export const documentResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  emailId: z.string().uuid().nullable().optional(),
  emailAttachmentId: z.string().uuid().nullable().optional(),

  // Full classification label set (mirrors tasks.labels). Single source
  // of truth for filtering and routing decisions.
  labels: z.array(z.string()).default([]),
  // Derived primary document_type — the first label whose category is
  // `document_type`. Computed server-side via selectPrimaryLabel.
  // Kept for backward-compat with consumers that render section
  // visibility per primary type; new code should read `labels` directly.
  classification: z.string().nullable().optional(),

  authNumber: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  urgency: z.string().nullable().optional(),

  patientName: z.string().nullable().optional(),
  patientDob: z.string().nullable().optional(),
  patientMemberId: z.string().nullable().optional(),
  patientPhone: z.string().nullable().optional(),
  patientAddress: z.string().nullable().optional(),
  patientId: z.string().uuid().nullable().optional(),

  referringProviderName: z.string().nullable().optional(),
  referringProviderNpi: z.string().nullable().optional(),
  referringProviderPhone: z.string().nullable().optional(),
  referringProviderFax: z.string().nullable().optional(),
  referringProviderEntityId: z.string().uuid().nullable().optional(),

  referringPracticeName: z.string().nullable().optional(),
  referringPracticeFax: z.string().nullable().optional(),
  referringPracticeEntityId: z.string().uuid().nullable().optional(),

  renderingProviderName: z.string().nullable().optional(),
  renderingProviderNpi: z.string().nullable().optional(),
  renderingProviderId: z.string().uuid().nullable().optional(),

  renderingPracticeName: z.string().nullable().optional(),
  renderingPracticeFax: z.string().nullable().optional(),

  pcpName: z.string().nullable().optional(),
  pcpNpi: z.string().nullable().optional(),
  pcpPhone: z.string().nullable().optional(),

  healthPlan: z.string().nullable().optional(),
  insuranceNetwork: z.string().nullable().optional(),
  payerEntityId: z.string().uuid().nullable().optional(),

  diagnosisCodes: z.array(diagnosisCodeSchema).nullable().optional(),
  serviceCodes: z.array(serviceCodeSchema).nullable().optional(),

  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  dateOfServiceFrom: z.string().nullable().optional(),
  dateOfServiceTo: z.string().nullable().optional(),

  documentDate: isoTimestamp,

  requestId: z.string().nullable().optional(),
  denialId: z.string().nullable().optional(),

  fileUrl: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),

  reviewStatus: z.string(),
  reviewedBy: z.string().uuid().nullable().optional(),
  reviewedAt: isoTimestamp.nullable().optional(),

  rowStatus: z.number(),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});

export type DocumentResponse = z.infer<typeof documentResponseSchema>;

export const updateDocumentSchema = z.object({
  authNumber: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  urgency: z.string().optional().nullable(),
  patientName: z.string().optional().nullable(),
  patientDob: z.string().optional().nullable(),
  patientMemberId: z.string().optional().nullable(),
  patientPhone: z.string().optional().nullable(),
  patientAddress: z.string().optional().nullable(),
  referringProviderName: z.string().optional().nullable(),
  referringProviderNpi: z.string().optional().nullable(),
  referringProviderPhone: z.string().optional().nullable(),
  referringProviderFax: z.string().optional().nullable(),
  referringPracticeName: z.string().optional().nullable(),
  referringPracticeFax: z.string().optional().nullable(),
  renderingProviderName: z.string().optional().nullable(),
  renderingProviderNpi: z.string().optional().nullable(),
  renderingPracticeName: z.string().optional().nullable(),
  renderingPracticeFax: z.string().optional().nullable(),
  pcpName: z.string().optional().nullable(),
  pcpNpi: z.string().optional().nullable(),
  pcpPhone: z.string().optional().nullable(),
  healthPlan: z.string().optional().nullable(),
  insuranceNetwork: z.string().optional().nullable(),
  diagnosisCodes: z.array(diagnosisCodeSchema).optional().nullable(),
  serviceCodes: z.array(serviceCodeSchema).optional().nullable(),
  validFrom: z.string().optional().nullable(),
  validTo: z.string().optional().nullable(),
  dateOfServiceFrom: z.string().optional().nullable(),
  dateOfServiceTo: z.string().optional().nullable(),
  requestId: z.string().optional().nullable(),
  denialId: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type UpdateDocumentRequest = z.infer<typeof updateDocumentSchema>;

export const reviewStatusRequestSchema = z.object({
  reviewStatus: documentReviewStatusSchema,
});

export type ReviewStatusRequest = z.infer<typeof reviewStatusRequestSchema>;

export const matchPatientSchema = z.object({
  patientId: z.string().uuid(),
});

export type MatchPatientRequest = z.infer<typeof matchPatientSchema>;

// ---------------------------------------------------------------------------
// Create-referral (structured-JSON document creation — Postman/API path).
// Mints a referral `document` from structured fields (no PDF / no extraction),
// then links a patient by one of three modes. See docs/referral-document-api.md.
// ---------------------------------------------------------------------------

export const createReferralPatientSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  /** Required by the `match` and `createInEhr` modes (validated server-side). */
  dateOfBirth: z.string().optional(),
  phone: z.string().optional(),
  memberId: z.string().optional(),
  address: z.string().optional(),
  /**
   * Sent to the EHR by the `createInEhr` mode only; not stored on the document.
   * Male or female only — athena records no other value for sex, and refusing one
   * here fails before the referral document is created rather than after.
   */
  gender: z.enum(['male', 'female']).optional(),
});

export const createReferralFieldsSchema = z.object({
  labels: z.array(z.string()).optional(),
  renderingProviderId: z.string().uuid().optional(),
  referringProviderName: z.string().optional(),
  referringProviderNpi: z.string().optional(),
  diagnosisCodes: z.array(diagnosisCodeSchema).optional(),
  serviceCodes: z.array(serviceCodeSchema).optional(),
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
  notes: z.string().optional(),
});

export const createReferralSchema = z.object({
  patient: createReferralPatientSchema,
  referral: createReferralFieldsSchema.optional(),
  /**
   * How to attach a patient:
   *   - existing    → link a known local patient by id
   *   - match       → real-time EHR match; link if found, else classify/create-local
   *   - createInEhr → create the patient in the EHR (Athena) then link
   * Optional — omitting it defaults to `match` (applied server-side, so the field
   * stays optional in the request type rather than being forced by a Zod default).
   */
  patientLink: z
    .discriminatedUnion('mode', [
      z.object({ mode: z.literal('existing'), patientId: z.string().uuid() }),
      z.object({ mode: z.literal('match') }),
      z.object({ mode: z.literal('createInEhr') }),
    ])
    .optional(),
});

export type CreateReferralRequest = z.infer<typeof createReferralSchema>;

export const createReferralResponseSchema = z.object({
  document: documentResponseSchema,
  /** The ReferralIntakeService outcome when `match` ran; null otherwise. */
  intakeStatus: z.string().nullable().optional(),
});

export type CreateReferralResponse = z.infer<typeof createReferralResponseSchema>;

// ---------------------------------------------------------------------------
// Extract-from-PDF (the "From PDF" mode of the New-referral form). Runs the
// same vision extractor the email-ingestion pipeline uses over an uploaded PDF
// and returns form-shaped fields for the dialog to prefill. NOTHING is
// persisted — the user reviews the fields, then `POST /api/documents` creates
// the referral + patient. See docs/referral-document-api.md.
// ---------------------------------------------------------------------------

export const referralExtractionSchema = z.object({
  patient: z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    /** Best-effort normalized to `yyyy-MM-dd`; omitted when unparseable. */
    dateOfBirth: z.string().optional(),
    phone: z.string().optional(),
    memberId: z.string().optional(),
    address: z.string().optional(),
  }),
  referral: z.object({
    referringProviderName: z.string().optional(),
    notes: z.string().optional(),
  }),
  /**
   * Overall extraction confidence (nominally 0..1), for surfacing a low-confidence
   * hint. Left unbounded so an out-of-range extractor value degrades to a useless
   * hint rather than failing the whole response validation.
   */
  confidence: z.number().nullable().optional(),
});

export type ReferralExtraction = z.infer<typeof referralExtractionSchema>;

export const matchEntitySchema = z.object({
  entityId: z.string().uuid(),
});

export type MatchEntityRequest = z.infer<typeof matchEntitySchema>;

export const matchProviderSchema = z.object({
  providerId: z.string().uuid(),
});

export type MatchProviderRequest = z.infer<typeof matchProviderSchema>;

// Kept multi-line: the pre-commit reflow is churn (see 8f414a6e).
// prettier-ignore
export type DocumentSortColumn =
  | 'documentDate'
  | 'patientName'
  | 'authNumber'
  | 'reviewStatus'
  | 'status'
  | 'urgency';

export interface DocumentSearchParams {
  status?: string;
  reviewStatus?: string;
  patientId?: string;
  searchTerm?: string;
  urgency?: string;
  classification?: string;
  referringProviderName?: string;
  healthPlan?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  sortBy?: DocumentSortColumn;
  sortOrder?: 'asc' | 'desc';
}

// =============================================================================
// Open-referral lookup (voice runtime)
// =============================================================================
